import React from "react"
import dynamic from "next/dynamic"
import { serverSideTranslations } from "next-i18next/serverSideTranslations"
import { i18n } from "next-i18next"
import {
  footerUrl,
  headerUrl,
  publicWebsiteUrl,
  COMPONENT_TYPES,
  PRODUCT_GLOBALCONFIG_DATA_URL,
  PRODUCT_DATA_URL,
} from "@/constants"
import { SELECT_AFTER_LAST_SLASH_REGEX } from "@/constants/regex"
import {
  generateCanonicalUrl,
  filterBackEndJson,
  getClienIP,
  updateAnalyticPageUrl,
} from "@/utils/helper"
import { isObjectEmpty, recursiveDataFunction } from "@/utils/"
import {
  getBundleProductDetailsFromServer,
  callGetConfigDataServerApi,
} from "@/services/productServices"
import {
  getAemServerAxiosInstance,
  getAemServerModelJsonAxiosInstance,
} from "@/services/server/aem.server"
import {
  getBVFullReviewData,
  getBVReviewStatsData,
  getProductDetailsFromServer,
} from "@/utils/product"
const BundlePdpPage = dynamic(() => import("@/components/BundlePdpPage"))
const ProductDetails = dynamic(() => import("@/components/ProductDetailsPage"))
const BundlePdp = props => {
  const { isMultiFinishBundle = false } = props || {}
  return isMultiFinishBundle ? (
    <BundlePdpPage {...props} />
  ) : (
    <ProductDetails {...props} />
  )
}

/**
 *
 * @return {Object}
 */
export async function getServerSideProps({
  req,
  resolvedUrl,
  locale,
  query = {},
}) {
  await i18n?.reloadResources()
  const serverAemApi = getAemServerAxiosInstance()
  const serverAemModelJsonApi = getAemServerModelJsonAxiosInstance()
  const { originalRequestPath = "", skuId = "" } = query
  const url = resolvedUrl

  const profileName = `profile_kohler_PDP`
  const collections = "kohler"
  const configUrl = url.replace(
    SELECT_AFTER_LAST_SLASH_REGEX,
    PRODUCT_GLOBALCONFIG_DATA_URL
  )
  const slug = originalRequestPath.split("/").at(-1) || ""
  const errorsData = {
    pdpData: null,
    pageModelError: null,
    headerModelError: null,
    footerModelError: null,
    configDataError: null,
  }
  let pdpData = {}
  const { data: configData = {}, error: configDataError } =
    await callGetConfigDataServerApi(locale, configUrl)
  const { general: { lwAppName = "" } = {} } = configData
  try {
    pdpData = await getBundleProductDetailsFromServer(slug, lwAppName)
  } catch (error) {
    const {
      response: {
        config: { url = "" } = {},
        status = 500,
        statusText = "",
      } = {},
    } = error
    errorsData.pdpData = {
      statusText,
      status,
      url,
    }
  }
  const { BundleSku_s: BundleSku } = pdpData
  const isMultiFinishBundle = Boolean(BundleSku)
  if (!isMultiFinishBundle) {
    try {
      pdpData = await getProductDetailsFromServer(
        profileName,
        collections,
        slug,
        locale
      )
    } catch (error) {
      const {
        response: {
          config: { url = "" } = {},
          status = 500,
          statusText = "",
        } = {},
      } = error
      errorsData.pdpData = {
        statusText,
        status,
        url,
      }
    }
  }
  if (isObjectEmpty(pdpData) && !errorsData.pdpData) {
    return {
      notFound: true,
    }
  }
  const pageModelUrl = `/${locale}${PRODUCT_DATA_URL}`
  let pageModelData = {}
  let pageModelError = null
  try {
    const response = await serverAemModelJsonApi({
      url: pageModelUrl,
      method: "GET",
    })
    pageModelData = response?.data || {}
  } catch (error) {
    const {
      response: {
        config: { headers, url = "" } = {},
        status = 500,
        statusText = "",
      } = {},
    } = error
    pageModelError = {
      headers,
      statusText,
      status,
      url,
    }
  }
  const {
    headerExpFrg = "",
    footerExpFrg = "",
    pageComponentProperties,
    data: rawAnalyticsData = "",
    seoProperties = {},
    globalScript = "",
    footerScript = "",
    datalayerPageName = "",
    eventPageType = "",
    siteName = "",
    pageType = "",
  } = pageModelData
  let header = {}
  let headerModelError = null
  const headerModelUrl = `${headerExpFrg || headerUrl}.model.json`
  try {
    const response = await serverAemApi({
      url: headerModelUrl,
      method: "GET",
    })
    header = response?.data || {}
  } catch (error) {
    const {
      response: {
        config: { headers, url = "" } = {},
        status = 500,
        statusText = "",
      } = {},
    } = error
    headerModelError = {
      headers,
      statusText,
      status,
      url,
    }
  }
  let footer = {}
  let footerModelError = null
  const footerModelUrl = `${footerExpFrg || footerUrl}.model.json`
  try {
    const response = await serverAemApi({
      url: footerModelUrl,
      method: "GET",
    })
    footer = response?.data || {}
  } catch (error) {
    const {
      response: {
        config: { headers, url = "" } = {},
        status = 500,
        statusText = "",
      } = {},
    } = error
    footerModelError = {
      headers,
      statusText,
      status,
      url,
    }
  }
  if (pageModelError) errorsData.pageModelError = pageModelError
  if (headerModelError) errorsData.headerModelError = headerModelError
  if (footerModelError) errorsData.footerModelError = footerModelError
  if (configDataError) errorsData.configDataError = configDataError
  const fullUrl = generateCanonicalUrl(url, locale, publicWebsiteUrl)
  const pageModelDataTmp = pageComponentProperties || pageModelData
  const tmpData = filterBackEndJson(pageModelDataTmp)
  const filterData = filterBackEndJson(tmpData)
  const authoredData =
    recursiveDataFunction(filterData, COMPONENT_TYPES.PRODUCT_DETAILS).at(0) ||
    {}
  const ip = getClienIP(req) ?? ""
  // Call BV API's after getting pdpData
  const reviewStatsData = await getBVReviewStatsData(skuId)
  const fullReviewData = await getBVFullReviewData(skuId)
  const analyticsData = updateAnalyticPageUrl(rawAnalyticsData, fullUrl)
  const gbhDataLayerCustom = {
    ...(datalayerPageName && {
      datalayerPageName,
    }),
    ...(eventPageType && {
      eventPageType,
    }),
    ...(pageType && {
      pageType,
    }),
  }
  return {
    props: {
      slug,
      pageModelData: tmpData,
      authoredData,
      header,
      footer,
      pdpData,
      errorsData,
      fullUrl,
      config: configData,
      rawAnalyticsData,
      seoProperties,
      isMultiFinishBundle,
      skuId,
      gbhDataLayerCustom,
      globalScript,
      footerScript,
      siteName,
      ip,
      analyticsData,
      reviewStatsData,
      fullReviewData,
      ...(await serverSideTranslations(locale, ["common"])),
    },
  }
}
export default BundlePdp
